// <i80-timeline>: our own component (not in Helios), made for zPlan. One day on a time
// axis: a coverage strip (needed vs planned, per time slot) over lanes of bars that can
// be dragged along the day, onto another lane, or stretched at either end.
//
// The component only draws and reports. Rules live on the server: a bar's status and note
// come in as attributes, and a drag fires a cancellable i80-timeline-change that the page
// (or HTMX) sends on; the server answers with the new markup.
import { I80Element, define, h } from "../base.js";
import { icon } from "../icons.js";
import css from "./timeline.css";

const COLORS = ["neutral", "blue", "purple", "green", "amber", "red"];
const PATTERNS = ["solid", "striped", "outline"];
const STATUSES = ["ok", "soft", "hard"];
const DAY = 1440;

const pad = (n) => String(n).padStart(2, "0");

/** "07:30" → 450, or null. */
function minutes(s) {
  const m = /^([0-2]?\d):([0-5]\d)$/.exec((s ?? "").trim());
  return m ? +m[1] * 60 + +m[2] : null;
}

function clock(min) {
  const t = ((min % DAY) + DAY) % DAY;
  return `${pad(Math.floor(t / 60))}:${pad(t % 60)}`;
}

/** 07:00 → "07", 07:30 → "07:30": the short form a bar has room for. */
function short(min) {
  const c = clock(min);
  return c.endsWith(":00") ? c.slice(0, 2) : c;
}

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

function ensureStyles() {
  if (document.querySelector("style[data-i80-timeline]")) return;
  const style = document.createElement("style");
  style.setAttribute("data-i80-timeline", "");
  style.textContent = css;
  document.head.append(style);
}

/**
 * <i80-timeline day-start="06:00" step="15" label="Wednesday 7 October">
 *   <i80-need from="06:00" to="07:00" count="2"></i80-need>
 *   <i80-lane key="open" label="Open shifts" no-coverage>
 *     <i80-bar id="s9" from="14:00" to="21:00" color="purple">AF</i80-bar>
 *   </i80-lane>
 *   <i80-lane key="p1" label="Ada" detail="Medication · 112/120 h">
 *     <i80-bar id="s1" from="07:00" to="15:00" color="blue" status="soft" note="10 h rest">DA</i80-bar>
 *   </i80-lane>
 * </i80-timeline>
 *
 * Needs that overlap add up. Bars in lanes without no-coverage count as planned. Events:
 * i80-timeline-change (cancellable; {bar, id, kind: move|resize, from, to, lane, previous})
 * and i80-timeline-select ({kind: bar|lane|gap, bar, id, lane, from, to, time, need, have}).
 */
export class I80Timeline extends I80Element {
  static get observedAttributes() {
    return ["day-start", "step", "label", "label-coverage", "label-short", "label-instructions", "hour-labels"];
  }

  setup() {
    ensureStyles();
    this.querySelector(':scope > [data-i80-part="timeline"]')?.remove();
    this._root = h("div", { class: "i80-timeline", "data-i80-part": "timeline" });
    this.append(this._root);
    this._source = new Map();
    this._root.addEventListener("pointerdown", (e) => this._down(e));
    this._root.addEventListener("pointermove", (e) => this._move(e));
    this._root.addEventListener("pointerup", (e) => this._up(e));
    this._root.addEventListener("pointercancel", () => this._cancel());
    this._root.addEventListener("click", (e) => this._click(e));
    this._root.addEventListener("keydown", (e) => this._key(e));
    this._observer = new MutationObserver((records) => {
      const relevant = (r) => {
        if (r.target === this) return r.type === "childList" && [...r.addedNodes, ...r.removedNodes].some((n) => n !== this._root);
        return !this._root.contains(r.target);
      };
      if (!this._drag && records.some(relevant)) this.update();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._observer?.disconnect();
  }

  get dayStart() {
    return minutes(this.getAttribute("day-start")) ?? 0;
  }

  get step() {
    const n = parseInt(this.getAttribute("step") ?? "15", 10);
    return [5, 10, 15, 30, 60].includes(n) ? n : 15;
  }

  /** Minutes since day-start; an end at or before its start runs past midnight. */
  _range(el) {
    const from = minutes(el.getAttribute("from"));
    const to = minutes(el.getAttribute("to"));
    if (from === null || to === null) return null;
    const a = (from - this.dayStart + DAY) % DAY;
    let b = (to - this.dayStart + DAY) % DAY;
    if (b <= a) b += DAY;
    return { a, b: Math.min(b, DAY) };
  }

  get needs() {
    return [...this.querySelectorAll(":scope > i80-need")].map((el) => ({ ...this._range(el), count: parseInt(el.getAttribute("count") ?? "1", 10) || 0 })).filter((n) => n.a !== undefined);
  }

  get lanes() {
    return [...this.querySelectorAll(":scope > i80-lane")].map((el) => ({
      el,
      key: el.getAttribute("key") ?? "",
      label: el.getAttribute("label") ?? "",
      detail: el.getAttribute("detail"),
      readonly: el.hasAttribute("readonly"),
      counts: !el.hasAttribute("no-coverage") && !el.hasAttribute("readonly"),
      marked: el.hasAttribute("marked"),
      bars: [...el.querySelectorAll(":scope > i80-bar")].map((b) => ({ el: b, ...this._range(b) })).filter((b) => b.a !== undefined),
    }));
  }

  /** Needed vs planned per step, merged into runs with the same numbers. */
  coverage() {
    const step = this.step;
    const needs = this.needs;
    const bars = this.lanes.filter((l) => l.counts).flatMap((l) => l.bars);
    const runs = [];
    for (let t = 0; t < DAY; t += step) {
      const need = needs.filter((n) => n.a <= t && t < n.b).reduce((s, n) => s + n.count, 0);
      const have = bars.filter((b) => b.a <= t && t < b.b).length;
      const last = runs[runs.length - 1];
      if (last && last.need === need && last.have === have) last.b = t + step;
      else runs.push({ a: t, b: t + step, need, have });
    }
    return runs;
  }

  update() {
    this._observer?.disconnect();
    try {
      this._render();
    } finally {
      if (this.isConnected) this._observer?.observe(this, { childList: true, subtree: true, attributes: true, characterData: true });
    }
  }

  _pct(min) {
    return `${(min / DAY) * 100}%`;
  }

  _render() {
    const refocus = this._refocus ?? (this._root.contains(document.activeElement) ? this._source.get(document.activeElement.closest?.(".i80-timeline__bar")) : null);
    this._refocus = null;
    this._source = new Map();
    const start = this.dayStart;
    const lanes = this.lanes;
    const runs = this.coverage();
    const max = Math.max(1, ...runs.map((r) => Math.max(r.need, r.have)));
    const labelShort = this.getAttribute("label-short") ?? "Short";
    const instructionsId = `${this.id || "i80-timeline"}-instructions`;
    const every = parseInt(this.getAttribute("hour-labels") ?? "2", 10) || 2;

    const axis = h("div", { class: "i80-timeline__axis", "aria-hidden": "true" },
      Array.from({ length: 24 / every }, (_, i) => h("span", { class: "i80-timeline__tick", style: `left:${this._pct(i * every * 60)}` }, short(start + i * every * 60))));

    const strip = h("div", { class: "i80-timeline__coverage", role: "group", "aria-label": this.getAttribute("label-coverage") ?? "Coverage" },
      runs.filter((r) => r.need || r.have).map((r) => {
        const state = r.have < r.need ? "short" : r.have > r.need ? "over" : "ok";
        const text = `${short(start + r.a)}–${short(start + r.b)}: ${r.have}/${r.need}`;
        const attrs = {
          class: ["i80-timeline__run", `i80-timeline__run--${state}`],
          style: `left:${this._pct(r.a)};width:${this._pct(r.b - r.a)};--i80-timeline-have:${r.have / max};--i80-timeline-need:${r.need / max}`,
          title: text,
        };
        return state === "short"
          ? h("button", { ...attrs, type: "button", "data-i80-gap": `${r.a}|${r.b}|${r.need}|${r.have}`, "aria-label": `${labelShort} ${text}` }, h("span", { class: "i80-timeline__fill" }))
          : h("span", { ...attrs, "aria-hidden": "true" }, h("span", { class: "i80-timeline__fill" }));
      }));

    const rows = lanes.map((lane) => {
      const track = h("div", { class: "i80-timeline__track", "data-i80-lane": lane.key },
        lane.bars.map((b) => this._bar(b, lane, instructionsId)));
      return h("div", { class: ["i80-timeline__lane", lane.readonly && "i80-timeline__lane--readonly", lane.marked && "i80-timeline__lane--marked", !lane.counts && !lane.readonly && "i80-timeline__lane--uncounted"], role: "listitem", "aria-label": lane.label },
        h("div", { class: "i80-timeline__lane-head" },
          h("span", { class: "i80-timeline__lane-label" }, lane.label),
          lane.detail && h("span", { class: "i80-timeline__lane-detail" }, lane.detail)),
        track);
    });

    this._root.replaceChildren(
      h("span", { id: instructionsId, class: "i80-timeline__sr", hidden: true },
        this.getAttribute("label-instructions") ?? "Left and right move the shift, Shift with left or right changes its end, up and down move it to another row."),
      h("div", { class: "i80-timeline__row i80-timeline__row--axis" }, h("div", { class: "i80-timeline__corner" }, this.getAttribute("label") ?? ""), axis),
      h("div", { class: "i80-timeline__row i80-timeline__row--coverage" },
        h("div", { class: "i80-timeline__lane-head" }, h("span", { class: "i80-timeline__lane-label" }, this.getAttribute("label-coverage") ?? "Coverage")),
        strip),
      h("div", { class: "i80-timeline__lanes", role: "list" }, rows),
    );
    if (refocus) [...this._source].find(([, src]) => src === refocus)?.[0].focus();
  }

  _bar(b, lane, instructionsId) {
    const el = b.el;
    const color = COLORS.includes(el.getAttribute("color")) ? el.getAttribute("color") : "neutral";
    const pattern = PATTERNS.includes(el.getAttribute("pattern")) ? el.getAttribute("pattern") : "solid";
    const status = STATUSES.includes(el.getAttribute("status")) ? el.getAttribute("status") : "ok";
    const note = el.getAttribute("note");
    const text = el.textContent.trim();
    const draggable = !lane.readonly && !el.hasAttribute("locked");
    const time = `${short(this.dayStart + b.a)}–${short(this.dayStart + b.b)}`;
    const btn = h("button", {
      type: "button",
      class: ["i80-timeline__bar", `i80-timeline__bar--${color}`, `i80-timeline__bar--${pattern}`, `i80-timeline__bar--${status}`,
        el.hasAttribute("selected") && "i80-timeline__bar--selected", draggable && "i80-timeline__bar--draggable"],
      style: `left:${this._pct(b.a)};width:${this._pct(b.b - b.a)}`,
      "aria-label": [text, `${clock(this.dayStart + b.a)}–${clock(this.dayStart + b.b)}`, lane.label, note].filter(Boolean).join(", "),
      "aria-pressed": String(el.hasAttribute("selected")),
      "aria-describedby": draggable ? instructionsId : null,
      title: note ? `${text} ${time} — ${note}` : `${text} ${time}`,
    },
    draggable && h("span", { class: "i80-timeline__handle i80-timeline__handle--start", "data-i80-handle": "start", "aria-hidden": "true" }),
    h("span", { class: "i80-timeline__bar-text" }, text),
    h("span", { class: "i80-timeline__bar-time", "data-i80-time": "" }, time),
    status !== "ok" && icon(status === "hard" ? "alert-octagon-fill" : "alert-triangle-fill", { className: "i80-timeline__bar-icon" }),
    draggable && h("span", { class: "i80-timeline__handle i80-timeline__handle--end", "data-i80-handle": "end", "aria-hidden": "true" }));
    this._source.set(btn, el);
    return btn;
  }

  // --- pointer: drag along the day, onto another lane, or by an end ---

  _down(e) {
    if (e.button !== 0) return;
    const btn = e.target.closest(".i80-timeline__bar--draggable");
    if (!btn) return;
    const src = this._source.get(btn);
    const r = this._range(src);
    this._drag = {
      btn, src, mode: e.target.closest("[data-i80-handle]")?.getAttribute("data-i80-handle") ?? "move",
      x0: e.clientX, y0: e.clientY, a: r.a, b: r.b, na: r.a, nb: r.b,
      width: btn.parentElement.getBoundingClientRect().width, home: btn.parentElement, track: btn.parentElement, moved: false,
    };
    btn.setPointerCapture?.(e.pointerId);
    btn.focus();
    e.preventDefault();
  }

  _move(e) {
    const d = this._drag;
    if (!d) return;
    const dx = e.clientX - d.x0;
    if (!d.moved && Math.hypot(dx, e.clientY - d.y0) < 4) return;
    d.moved = true;
    d.btn.classList.add("i80-timeline__bar--dragging");
    const step = this.step;
    const dm = Math.round(((dx / d.width) * DAY) / step) * step;
    if (d.mode === "move") {
      d.na = clamp(d.a + dm, 0, DAY - (d.b - d.a));
      d.nb = d.na + (d.b - d.a);
      const track = document.elementsFromPoint(e.clientX, e.clientY)
        .find((el) => el.classList?.contains("i80-timeline__track") && this._root.contains(el) && !el.closest(".i80-timeline__lane--readonly"));
      if (track && track !== d.track) {
        d.track = track;
        track.append(d.btn);
      }
    } else if (d.mode === "start") {
      d.na = clamp(d.a + dm, 0, d.b - step);
    } else {
      d.nb = clamp(d.b + dm, d.a + step, DAY);
    }
    d.btn.style.left = this._pct(d.na);
    d.btn.style.width = this._pct(d.nb - d.na);
    d.btn.querySelector("[data-i80-time]").textContent = `${short(this.dayStart + d.na)}–${short(this.dayStart + d.nb)}`;
    for (const t of this._root.querySelectorAll(".i80-timeline__track--target")) t.classList.remove("i80-timeline__track--target");
    if (d.track !== d.home) d.track.classList.add("i80-timeline__track--target");
  }

  _up() {
    const d = this._drag;
    if (!d) return;
    this._drag = null;
    if (!d.moved) return;
    this._suppressClick = true;
    setTimeout(() => (this._suppressClick = false));
    const lane = this.lanes.find((l) => l.key === d.track.getAttribute("data-i80-lane"))?.el;
    this._commit(d.src, lane, d.na, d.nb, d.mode === "move" ? "move" : "resize");
  }

  _cancel() {
    if (!this._drag) return;
    this._drag = null;
    this.update();
  }

  /** Fire i80-timeline-change; unless it is cancelled, write the change into the markup. */
  _commit(src, lane, a, b, kind) {
    const from = clock(this.dayStart + a);
    const to = clock(this.dayStart + b);
    const home = src.parentElement;
    const previous = { lane: home.getAttribute("key"), from: src.getAttribute("from"), to: src.getAttribute("to") };
    this._refocus = src;
    if (lane === home && previous.from === from && previous.to === to) {
      this.update();
      return;
    }
    const ok = this.dispatchEvent(new CustomEvent("i80-timeline-change", {
      bubbles: true, cancelable: true,
      detail: { bar: src, id: src.id || null, kind: lane !== home ? "move" : kind, from, to, lane: lane.getAttribute("key"), previous },
    }));
    if (ok && src.isConnected) {
      src.setAttribute("from", from);
      src.setAttribute("to", to);
      if (lane !== home) lane.append(src);
    }
    this._refocus = src;
    this.update();
  }

  // --- click and keyboard ---

  _click(e) {
    if (this._suppressClick) return;
    const bar = e.target.closest(".i80-timeline__bar");
    const gap = e.target.closest("[data-i80-gap]");
    const track = e.target.closest(".i80-timeline__track");
    let detail = null;
    if (bar) {
      const src = this._source.get(bar);
      detail = { kind: "bar", bar: src, id: src.id || null, lane: src.parentElement.getAttribute("key"), from: src.getAttribute("from"), to: src.getAttribute("to") };
    } else if (gap) {
      const [a, b, need, have] = gap.getAttribute("data-i80-gap").split("|").map(Number);
      detail = { kind: "gap", from: clock(this.dayStart + a), to: clock(this.dayStart + b), need, have };
    } else if (track) {
      const r = track.getBoundingClientRect();
      const t = Math.floor((((e.clientX - r.left) / r.width) * DAY) / this.step) * this.step;
      detail = { kind: "lane", lane: track.getAttribute("data-i80-lane"), time: clock(this.dayStart + clamp(t, 0, DAY - this.step)) };
    }
    if (detail) this.dispatchEvent(new CustomEvent("i80-timeline-select", { bubbles: true, detail }));
  }

  _key(e) {
    const btn = e.target.closest?.(".i80-timeline__bar--draggable");
    if (!btn) return;
    if (e.key === "Escape" && this._drag) {
      this._cancel();
      return;
    }
    const src = this._source.get(btn);
    const r = this._range(src);
    const step = this.step;
    const lanes = this.lanes.filter((l) => !l.readonly);
    const i = lanes.findIndex((l) => l.el === src.parentElement);
    let move = null;
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      const dm = e.key === "ArrowRight" ? step : -step;
      if (e.shiftKey) move = [src.parentElement, r.a, clamp(r.b + dm, r.a + step, DAY), "resize"];
      else {
        const a = clamp(r.a + dm, 0, DAY - (r.b - r.a));
        move = [src.parentElement, a, a + (r.b - r.a), "move"];
      }
    } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      const target = lanes[i + (e.key === "ArrowDown" ? 1 : -1)];
      if (target) move = [target.el, r.a, r.b, "move"];
    }
    if (!move) return;
    e.preventDefault();
    this._commit(src, ...move);
  }
}

define("i80-timeline", I80Timeline);
